"use client"

import { useEffect, useState } from "react"
import Image from "next/image"
import { useRouter } from "next/navigation"
import { useTheme } from "@/components/ThemeProvider"
import { NoSlotButton, NoSlotConfirmation, type NoSlotResult } from "@/components/rdv/NoSlotButton"
import { formatPhone, normalizePhone } from "@/lib/phone"
import { TYPES, type NoSlotRequest, type Slot } from "./_types"

const sans = "var(--font-sans), DM Sans, sans-serif"

interface TokenInfo {
  valid: boolean
  alreadyBooked?: boolean
  companyPhone?: string | null
  isPro?: boolean
  residenceName?: string
  residenceAddress?: string
  date?: string
  dateLabel?: string
  /** Renseigné quand la journée résidence couvre une période */
  periodLabel?: string | null
  days?: { dayKey: string; label: string; startTime: string; endTime: string }[]
  startTime?: string
  endTime?: string
}

interface ClientData {
  companyName?: string
  firstName: string
  lastName: string
  phone: string
  email: string
  address: string
  residenceName: string
  bat: string
  floor: string
  postalCode: string
  city: string
  accessCode: string
}

export function CampaignFlow({ campaignToken }: { campaignToken: string }) {
  const router = useRouter()
  const { theme } = useTheme()

  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null)
  const [loadingToken, setLoadingToken] = useState(true)
  const [alreadyBooked, setAlreadyBooked] = useState(false)

  // Step 1 — identity
  const [nameInput, setNameInput] = useState("")
  const [nameError, setNameError] = useState("")
  const [verifying, setVerifying] = useState(false)

  // Step 2 — booking
  const [sessionToken, setSessionToken] = useState<string | null>(null)
  const [clientData, setClientData] = useState<ClientData | null>(null)
  const [targetDate, setTargetDate] = useState<string | null>(null)
  const [residenceAddress, setResidenceAddress] = useState<string | null>(null)
  const [type, setType] = useState("ENTRETIEN")
  const [description, setDescription] = useState("")
  const [slots, setSlots] = useState<Slot[]>([])
  const [slotDuration, setSlotDuration] = useState(60)
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null)
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState("")
  const [typeError, setTypeError] = useState("")
  const [noSlotDone, setNoSlotDone] = useState<NoSlotResult | null>(null)

  useEffect(() => {
    fetch(`/api/rdv/campaign-token/${campaignToken}`)
      .then((r) => r.json())
      .then((data: TokenInfo) => {
        setTokenInfo(data)
        if (data.alreadyBooked) setAlreadyBooked(true)
      })
      .catch(() => setTokenInfo({ valid: false }))
      .finally(() => setLoadingToken(false))
  }, [campaignToken])

  async function handleVerify() {
    if (!nameInput.trim()) {
      setNameError("Merci de saisir votre nom")
      return
    }
    setVerifying(true)
    setNameError("")
    const res = await fetch("/api/rdv/campaign-verify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: campaignToken, name: nameInput }),
    })
    const data = await res.json()
    if (!res.ok) {
      if (res.status === 409) {
        setAlreadyBooked(true)
        setVerifying(false)
        return
      }
      setNameError(
        res.status === 429
          ? "Trop de tentatives. Réessayez dans 10 minutes."
          : "Nom invalide. Vérifiez l'orthographe et réessayez."
      )
      setVerifying(false)
      return
    }
    setSessionToken(data.sessionToken)
    setClientData(data.client)
    setTargetDate(data.date)
    setResidenceAddress(data.residenceAddress)
    setVerifying(false)
    fetchSlots(data.sessionToken, data.residenceAddress, data.date)
  }

  async function fetchSlots(sToken: string, address: string, date: string) {
    setLoadingSlots(true)
    const params = new URLSearchParams({ address, sessionToken: sToken, targetDate: date })
    const res = await fetch(`/api/rdv/slots?${params}`)
    const data = await res.json()
    setSlots(data.slots ?? [])
    setSlotDuration(data.slotDuration ?? 60)
    setLoadingSlots(false)
  }

  async function handleSubmit() {
    if (!type) {
      setTypeError("Choisissez un type d'intervention")
      return
    }
    if (!selectedSlot) return
    if (!clientData || !sessionToken) return
    setSubmitting(true)
    const res = await fetch("/api/rdv", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...clientData,
        type,
        description,
        date: selectedSlot,
        consent: true,
        sessionToken,
      }),
    })
    if (res.ok) {
      const data = await res.json()
      router.push(`/rdv/confirmation/${data.token}?new=1`)
    } else {
      const data = await res.json().catch(() => ({}))
      setSubmitError(
        res.status === 409
          ? "Ce créneau vient d'être pris. Choisissez-en un autre."
          : res.status === 422
            ? "Ce créneau n'est plus disponible."
            : (data?.error ?? "Une erreur est survenue. Réessayez.")
      )
      setSubmitting(false)
    }
  }

  const logo =
    theme === "dark" ? "/assets/LJS PLOMBERIE_logo_clair.png" : "/assets/LJSPLOMBERIE_logo.png"

  if (loadingToken) {
    return (
      <Shell logo={logo}>
        <p
          style={{
            fontFamily: sans,
            fontSize: 14,
            color: "var(--ljs-text-faint)",
            textAlign: "center",
          }}
        >
          Chargement…
        </p>
      </Shell>
    )
  }

  if (!tokenInfo?.valid) {
    return (
      <Shell logo={logo}>
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <p
            style={{
              fontFamily: sans,
              fontSize: 16,
              fontWeight: 600,
              color: "var(--ljs-text)",
              marginBottom: 8,
            }}
          >
            Lien expiré ou invalide
          </p>
          <p style={{ fontFamily: sans, fontSize: 13, color: "var(--ljs-text-dim)" }}>
            Ce lien de réservation n&apos;est plus valide.
            {tokenInfo?.companyPhone ? (
              <>
                {" "}
                Contactez-nous au{" "}
                <a
                  href={`tel:${normalizePhone(tokenInfo.companyPhone)}`}
                  style={{ color: "#0EA5E9" }}
                >
                  {formatPhone(tokenInfo.companyPhone)}
                </a>
                .
              </>
            ) : null}
          </p>
        </div>
      </Shell>
    )
  }

  if (alreadyBooked) {
    return (
      <Shell logo={logo}>
        <div
          style={{
            padding: "20px 16px",
            background: "rgba(249,115,22,0.08)",
            border: "1px solid rgba(249,115,22,0.25)",
            borderRadius: 12,
            textAlign: "center",
          }}
        >
          <p
            style={{
              fontFamily: sans,
              fontSize: 15,
              fontWeight: 700,
              color: "#F97316",
              margin: "0 0 6px",
            }}
          >
            Rendez-vous déjà réservé
          </p>
          <p style={{ fontFamily: sans, fontSize: 13, color: "var(--ljs-text-dim)", margin: 0 }}>
            {tokenInfo.periodLabel ? (
              <>
                Vous avez déjà un rendez-vous prévu pour l&apos;intervention{" "}
                <strong>{tokenInfo.periodLabel}</strong>. Un seul rendez-vous est possible sur
                l&apos;ensemble de la période.
              </>
            ) : (
              <>
                Vous avez déjà un rendez-vous prévu pour la journée du{" "}
                <strong>{tokenInfo.dateLabel}</strong>. Un seul rendez-vous est possible par journée
                d&apos;intervention.
              </>
            )}
          </p>
        </div>
      </Shell>
    )
  }

  if (noSlotDone) {
    return (
      <Shell logo={logo}>
        <NoSlotConfirmation alreadyRegistered={noSlotDone.alreadyRegistered} />
      </Shell>
    )
  }

  const days = tokenInfo.days ?? []
  const selectedDay = days.find((d) => d.dayKey === targetDate) ?? null

  const noSlotPayload: NoSlotRequest | null =
    clientData && sessionToken && targetDate
      ? {
          noSlotAccessType: "RESIDENCE_DAY",
          residenceDayDate: targetDate,
          companyName: clientData.companyName,
          firstName: clientData.firstName,
          lastName: clientData.lastName,
          phone: clientData.phone,
          email: clientData.email,
          address: clientData.address,
          residenceName: clientData.residenceName,
          bat: clientData.bat,
          floor: clientData.floor,
          postalCode: clientData.postalCode,
          city: clientData.city,
          type,
          description,
          consent: true,
          sessionToken,
        }
      : null

  return (
    <Shell logo={logo}>
      {/* Context banner */}
      <div
        style={{
          padding: "12px 16px",
          background: "rgba(249,115,22,0.08)",
          border: "1px solid rgba(249,115,22,0.25)",
          borderRadius: 10,
          marginBottom: 20,
        }}
      >
        <p
          style={{
            fontFamily: sans,
            fontSize: 12,
            fontWeight: 700,
            color: "#F97316",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            margin: "0 0 2px",
          }}
        >
          Intervention LJS Plomberie
        </p>
        <p
          style={{
            fontFamily: sans,
            fontSize: 14,
            fontWeight: 600,
            color: "var(--ljs-text)",
            margin: "0 0 2px",
          }}
        >
          {tokenInfo.residenceName}
        </p>
        <p style={{ fontFamily: sans, fontSize: 13, color: "var(--ljs-text-dim)", margin: 0 }}>
          {tokenInfo.periodLabel
            ? `Intervention ${tokenInfo.periodLabel}`
            : `${tokenInfo.dateLabel} · ${tokenInfo.startTime}–${tokenInfo.endTime}`}
        </p>
      </div>

      {/* Step 1 — Name verification */}
      {!sessionToken && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <p
              style={{
                fontFamily: sans,
                fontSize: 14,
                fontWeight: 600,
                color: "var(--ljs-text)",
                margin: "0 0 6px",
              }}
            >
              {tokenInfo.isPro ? "Saisissez votre raison sociale" : "Saisissez votre prénom et nom"}
            </p>
            <p
              style={{
                fontFamily: sans,
                fontSize: 13,
                color: "var(--ljs-text-dim)",
                margin: "0 0 14px",
              }}
            >
              Cette étape confirme votre identité pour accéder à la prise de rendez-vous.
            </p>
            <input
              value={nameInput}
              onChange={(e) => {
                setNameInput(e.target.value)
                setNameError("")
              }}
              onKeyDown={(e) => e.key === "Enter" && handleVerify()}
              placeholder={tokenInfo.isPro ? "Nom de votre entreprise" : "Prénom Nom"}
              autoFocus
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: 10,
                border: `1px solid ${nameError ? "#EF4444" : "var(--ljs-border)"}`,
                background: "var(--ljs-surface)",
                color: "var(--ljs-text)",
                fontFamily: sans,
                fontSize: 15,
                boxSizing: "border-box",
                outline: "none",
              }}
            />
            {nameError && (
              <p style={{ fontFamily: sans, fontSize: 12, color: "#EF4444", margin: "6px 0 0" }}>
                {nameError}
              </p>
            )}
          </div>
          <button onClick={handleVerify} disabled={verifying} style={btnPrimary}>
            {verifying ? "Vérification…" : "Confirmer mon identité →"}
          </button>
        </div>
      )}

      {/* Step 2 — Booking */}
      {sessionToken && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div>
            <p
              style={{
                fontFamily: sans,
                fontSize: 13,
                fontWeight: 700,
                color: "var(--ljs-text)",
                margin: "0 0 10px",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Type d&apos;intervention
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {TYPES.map((t) => (
                <button
                  key={t.value}
                  onClick={() => {
                    setType(t.value)
                    setTypeError("")
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 14px",
                    borderRadius: 10,
                    border: `1px solid ${type === t.value ? "#0EA5E9" : "var(--ljs-border)"}`,
                    background: type === t.value ? "rgba(14,165,233,0.08)" : "var(--ljs-surface)",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <span style={{ fontSize: 20 }}>{t.icon}</span>
                  <div>
                    <div
                      style={{
                        fontFamily: sans,
                        fontSize: 14,
                        fontWeight: 600,
                        color: "var(--ljs-text)",
                      }}
                    >
                      {t.label}
                    </div>
                    <div style={{ fontFamily: sans, fontSize: 12, color: "var(--ljs-text-dim)" }}>
                      {t.desc}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            {typeError && (
              <p style={{ fontFamily: sans, fontSize: 12, color: "#EF4444", margin: "6px 0 0" }}>
                {typeError}
              </p>
            )}
          </div>

          <div>
            <p
              style={{
                fontFamily: sans,
                fontSize: 13,
                fontWeight: 700,
                color: "var(--ljs-text)",
                margin: "0 0 8px",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Description (optionnelle)
            </p>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Décrivez brièvement le problème…"
              rows={3}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid var(--ljs-border)",
                background: "var(--ljs-surface)",
                color: "var(--ljs-text)",
                fontFamily: sans,
                fontSize: 13,
                boxSizing: "border-box",
                resize: "vertical",
              }}
            />
          </div>

          <div>
            <p
              style={{
                fontFamily: sans,
                fontSize: 13,
                fontWeight: 700,
                color: "var(--ljs-text)",
                margin: "0 0 10px",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Choisir un créneau
              {selectedDay ? ` — ${selectedDay.label}` : ` — ${tokenInfo.dateLabel}`}
            </p>

            {/* Période : le client choisit d'abord son jour */}
            {days.length > 1 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
                {days.map((d) => {
                  const active = d.dayKey === targetDate
                  return (
                    <button
                      key={d.dayKey}
                      onClick={() => {
                        setTargetDate(d.dayKey)
                        if (sessionToken && residenceAddress)
                          fetchSlots(sessionToken, residenceAddress, d.dayKey)
                      }}
                      style={{
                        background: active ? "#1D4ED8" : "rgba(29,78,216,0.06)",
                        color: active ? "#fff" : "#1D4ED8",
                        fontFamily: sans,
                        fontSize: 12,
                        fontWeight: 600,
                        padding: "7px 12px",
                        borderRadius: 8,
                        border: active ? "none" : "1px solid rgba(29,78,216,0.2)",
                        cursor: "pointer",
                        textTransform: "capitalize",
                      }}
                    >
                      {d.label}
                      <span
                        style={{
                          display: "block",
                          fontSize: 10,
                          fontWeight: 500,
                          opacity: 0.8,
                          textTransform: "none",
                        }}
                      >
                        {d.startTime}–{d.endTime}
                      </span>
                    </button>
                  )
                })}
              </div>
            )}
            {loadingSlots ? (
              <p style={{ fontFamily: sans, fontSize: 13, color: "var(--ljs-text-faint)" }}>
                Chargement des créneaux…
              </p>
            ) : slots.length === 0 ? (
              <p style={{ fontFamily: sans, fontSize: 13, color: "var(--ljs-text-faint)" }}>
                Aucun créneau disponible ce jour.
              </p>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 8,
                }}
              >
                {slots.map((s) => (
                  <button
                    key={s.date}
                    onClick={() => setSelectedSlot(s.date)}
                    style={{
                      padding: "10px 8px",
                      borderRadius: 8,
                      border: `1px solid ${selectedSlot === s.date ? "#0EA5E9" : "var(--ljs-border)"}`,
                      background:
                        selectedSlot === s.date ? "rgba(14,165,233,0.12)" : "var(--ljs-surface)",
                      color: selectedSlot === s.date ? "#0EA5E9" : "var(--ljs-text)",
                      fontFamily: sans,
                      fontSize: 14,
                      fontWeight: selectedSlot === s.date ? 600 : 400,
                      cursor: "pointer",
                      textAlign: "center",
                    }}
                  >
                    {s.time}
                    <span
                      style={{ display: "block", fontSize: 11, color: "var(--ljs-text-faint)" }}
                    >
                      {slotDuration} min
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {selectedSlot && (
            <>
              {submitError && (
                <p style={{ fontFamily: sans, fontSize: 13, color: "#EF4444", margin: "0 0 4px" }}>
                  {submitError}
                </p>
              )}
              <button
                onClick={() => {
                  setSubmitError("")
                  handleSubmit()
                }}
                disabled={submitting}
                style={btnPrimary}
              >
                {submitting ? "Réservation en cours…" : "Confirmer le rendez-vous →"}
              </button>
            </>
          )}

          {/* Sortie alternative — aucun créneau ne convient */}
          {noSlotPayload && (
            <div
              style={{
                borderTop: "1px solid var(--ljs-border)",
                paddingTop: 16,
              }}
            >
              <p
                style={{
                  fontFamily: sans,
                  fontSize: 12,
                  color: "var(--ljs-text-faint)",
                  margin: "0 0 10px",
                }}
              >
                Aucun de ces créneaux ne vous convient ?
              </p>
              <NoSlotButton payload={noSlotPayload} onDone={setNoSlotDone} disabled={submitting} />
            </div>
          )}
        </div>
      )}
    </Shell>
  )
}

function Shell({ logo, children }: { logo: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--ljs-bg)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "24px 16px",
      }}
    >
      <div style={{ width: "100%", maxWidth: 480 }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 28 }}>
          <Image
            src={logo}
            alt="LJS Plomberie"
            width={120}
            height={40}
            style={{ objectFit: "contain" }}
          />
        </div>
        {children}
      </div>
    </div>
  )
}

const btnPrimary: React.CSSProperties = {
  width: "100%",
  padding: "14px",
  borderRadius: 10,
  border: "none",
  background: "#0EA5E9",
  color: "#fff",
  fontFamily: sans,
  fontSize: 15,
  fontWeight: 700,
  cursor: "pointer",
}
